import * as moment from 'moment';
import {Observable} from 'rxjs';
import {Filter} from '../../../api/filter-interface';
import {FilterAndData} from '../../../api/filter-list';
import {Uri} from '../../../api/uri';
import {Resource} from '../../../api/resource';
import {InternalPropertyMap} from '../../../basic-entity-back/basic-entity-interface/mapping-internal';
import {
    AfterDateFilter, BeforeDateFilter, BetweenDateFilter, DateFilters, StrictlyAfterDateFilter, StrictlyBeforeDateFilter
} from '../../../basic-entity-back/filters/date-filter';
import {GeneralSearchFilter} from '../../../basic-entity-back/filters/general-search-filter';
import {botonAplicado, sinFiltrosDeBoton} from '../filtros-botones';

/** Una casilla: lo que se lee y lo que va a la API */
export interface OpcionFiltro {
    etiqueta: string;
    valor: string;
}

/**
 * Los tipos de sección: casillas (valores fijos o de un catálogo), texto abierto, fechas desde / hasta, Sí / No y
 * buscador con sugerencias (relaciones con demasiados valores para casillas, p. ej. Cliente).
 */
export type TipoSeccion = 'casillas' | 'texto' | 'fechas' | 'sino' | 'buscador';

/**
 * Una sección del panel de filtros. `propiedad` es el modelKey de una propiedad del modelo de la tabla, y su
 * `paramName` es lo que llega a la API: el parámetro tiene que existir allí, con ese mismo tipo de filtro.
 * La configuración es explícita por tabla a propósito: los filtros declarados en los modelos no siempre los
 * admite la API (en Piezas, tres no lo hacían), y declararlos aquí obliga a revisarlos.
 */
export interface SeccionFiltro {
    titulo: string;
    tipo: TipoSeccion;
    propiedad: string;
    filtro: Filter;
    /** En las de casillas; en el buscador, si las hay, dan la etiqueta de un valor elegido */
    opciones?: OpcionFiltro[];
    /** Solo en las de texto: deja el texto en lo que la API entiende (p. ej. solo dígitos en un id). Si no queda nada, no filtra */
    limpiar?: (texto: string) => string;
    /**
     * Solo en las de casillas, para listas largas: los valores que se ven de entrada; el resto queda tras
     * «Ver todos». Sin definir, se ven todas. Vacía (p. ej. ningún material tiene piezas) se ven las
     * primeras MAX_SIN_DESTACADAS, para que la sección no salga vacía. Una casilla marcada se ve siempre.
     */
    destacadas?: string[];
    /**
     * Solo en las de casillas: los valores que se marcan al abrir la tabla sin ningún filtro en la URL. Si el
     * usuario lo quita todo, la URL lleva `cleared` y no vuelven en esa visita.
     */
    porDefecto?: string[];
    /** Convierte cada valor (string) a lo que espera el filtro: Uri para relaciones, boolean para «existe»… */
    convertir?: (valor: string) => any;
    /** Solo buscador: sugerencias a partir de un texto, y la etiqueta de un valor ya elegido */
    sugerencias?: (texto: string) => Observable<OpcionFiltro[]>;
    etiquetaDe?: (valor: string) => Observable<string>;
    /**
     * Solo en las casillas de una relación del panel generado: carga sus opciones, que llegan después de crear la
     * sección (nace con `opciones: []`). `conOpcionesCargadas` (generar-secciones.ts) las pide y emite las secciones
     * con ellas, en un array nuevo.
     */
    cargarOpciones?: () => Observable<OpcionFiltro[]>;
}

/** Lo elegido en una sección de fechas, en ISO `YYYY-MM-DD`; null = sin límite por ese lado */
export interface RangoFechas {
    desde: string | null;
    hasta: string | null;
}

/**
 * Lo marcado o escrito en cada sección, por `propiedad`: casillas y buscador → sus valores (en relaciones, IRIs);
 * texto → el texto; fechas → `{desde, hasta}`; Sí / No → `'si'` y/o `'no'`
 */
export type ValoresPanel = { [propiedad: string]: string[] | string | RangoFechas };

/** Una etiqueta de «Filtros aplicados» */
export interface FiltroAplicado {
    seccion: SeccionFiltro;
    valor: string;
    texto: string;
}

const esFiltroDeFecha = (filtro: FilterAndData) => DateFilters.includes(filtro.filter);

/**
 * Si un filtro es de la sección: misma propiedad y mismo filtro. En las de fechas vale cualquiera de fecha de esa
 * propiedad: el panel manda after / before, pero la URL puede traer un between (o los estrictos).
 */
const esDeLaSeccion = (seccion: SeccionFiltro, filtro: FilterAndData) =>
    filtro.property?.modelKey === seccion.propiedad
    && (seccion.tipo === 'fechas' ? esFiltroDeFecha(filtro) : filtro.filter === seccion.filtro);

/** Las secciones cuyo valor es una lista de strings */
const esDeLista = (seccion: SeccionFiltro) => ['casillas', 'sino', 'buscador'].includes(seccion.tipo);

const SIN_FECHAS: RangoFechas = {desde: null, hasta: null};

/** Las dos casillas de una sección de Sí / No */
export const OPCIONES_SINO: OpcionFiltro[] = [{etiqueta: 'Sí', valor: 'si'}, {etiqueta: 'No', valor: 'no'}];

/** Un valor de la URL como string: una relación (Uri o Resource) es su IRI */
const valorComoTexto = (dato: any): string =>
    dato instanceof Uri ? dato.toString() : (dato instanceof Resource ? dato.iri.toString() : String(dato));

/** Un dato de un filtro de Sí / No leído de la URL: BooleanFilter acepta también "true"/"1"; ExistsFilter, cualquier verdad */
const esVerdadero = (dato: any): boolean => dato === 'false' || dato === '0' ? false : !!dato;

/** Una fecha (moment, Date o texto) como `YYYY-MM-DD`, o null si no lo es */
export function diaIso(dato: any): string | null {
    if (dato == null || dato === '') {
        return null;
    }
    const m = moment.isMoment(dato) ? dato : moment(dato);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/** El desde / hasta de una sección de fechas a partir de sus filtros (after, before, sus estrictos o un between) */
function fechasDesdeFiltros(filtros: FilterAndData[]): RangoFechas {
    const rango: RangoFechas = {...SIN_FECHAS};
    for (const filtro of filtros) {
        const datos = filtro.data;
        if (filtro.filter === AfterDateFilter || filtro.filter === StrictlyAfterDateFilter) {
            rango.desde = diaIso(datos) ?? rango.desde;
        } else if (filtro.filter === BeforeDateFilter || filtro.filter === StrictlyBeforeDateFilter) {
            rango.hasta = diaIso(datos) ?? rango.hasta;
        } else if (filtro.filter === BetweenDateFilter && Array.isArray(datos) && datos.length === 2) {
            rango.desde = diaIso(datos[0]) ?? rango.desde;
            rango.hasta = diaIso(datos[1]) ?? rango.hasta;
        }
    }
    return rango;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY`, para las etiquetas */
const diaLegible = (iso: string) => moment(iso, 'YYYY-MM-DD').format('DD/MM/YYYY');

/**
 * Computa el texto limpio de una sección de texto: aplica la función limpiar (si existe)
 * y recorta espacios en blanco. Devuelve cadena vacía si no queda nada. Exportada para que el panel
 * sepa si lo tecleado y lo que trae la URL son el mismo filtro.
 */
export const limpiarTexto = (seccion: SeccionFiltro, raw: string): string => {
    const texto = (seccion.limpiar ? seccion.limpiar(raw) : raw).trim();
    return texto;
};

/** Lo que pinta el panel a partir de los filtros de la URL; lo que no es de ninguna sección se ignora */
export function valoresDesdeFiltros(secciones: SeccionFiltro[], filtros: FilterAndData[]): ValoresPanel {
    const valores: ValoresPanel = {};
    for (const seccion of secciones) {
        if (seccion.tipo === 'fechas') {
            valores[seccion.propiedad] = fechasDesdeFiltros(filtros.filter(f => esDeLaSeccion(seccion, f)));
            continue;
        }
        const filtro = filtros.find(f => esDeLaSeccion(seccion, f));
        const datos = filtro?.data;
        if (seccion.tipo === 'sino') {
            valores[seccion.propiedad] = datos == null ? [] : [esVerdadero(datos) ? 'si' : 'no'];
        } else if (esDeLista(seccion)) {
            valores[seccion.propiedad] = datos == null ? [] : (Array.isArray(datos) ? datos : [datos]).map(valorComoTexto);
        } else {
            valores[seccion.propiedad] = datos == null ? '' : String(datos);
        }
    }
    return valores;
}

/**
 * Los filtros del panel, de 0 a n por sección. Una casilla (o un elegido del buscador) va suelta y varias en array
 * (la API las recibe como `param[]`, con O), cada una pasada por `convertir`; el texto va recortado y, vacío, no
 * filtra; Sí / No filtra con un boolean solo si hay una marcada; las fechas van como after (principio del día de
 * `desde`) y before (final del día de `hasta`): el DateFilter de API Platform no entiende between.
 */
export function filtrosDesdeValores(secciones: SeccionFiltro[], valores: ValoresPanel,
                                    propiedades: InternalPropertyMap[]): FilterAndData[] {
    const filtros: FilterAndData[] = [];
    for (const seccion of secciones) {
        const propiedad = propiedades.find(p => p.modelKey === seccion.propiedad);
        if (!propiedad) {
            throw new Error(`Panel de filtros: la propiedad "${seccion.propiedad}" no está en el modelo de la tabla`);
        }
        const valor = valores[seccion.propiedad];
        const convertir = seccion.convertir || ((v: string) => v);
        if (seccion.tipo === 'fechas') {
            const {desde, hasta} = (valor as RangoFechas) || SIN_FECHAS;
            if (desde) {
                filtros.push(new FilterAndData(AfterDateFilter, moment(desde, 'YYYY-MM-DD').startOf('day'), propiedad));
            }
            if (hasta) {
                filtros.push(new FilterAndData(BeforeDateFilter, moment(hasta, 'YYYY-MM-DD').endOf('day'), propiedad));
            }
        } else if (seccion.tipo === 'sino') {
            // Las dos marcadas o ninguna: no filtra. El dato es boolean (en ExistsFilter, "false" sería verdad)
            const marcados = (valor as string[]) || [];
            if (marcados.length === 1) {
                const [marcado] = marcados;
                filtros.push(new FilterAndData(seccion.filtro,
                    seccion.convertir ? seccion.convertir(marcado) : marcado === 'si', propiedad));
            }
        } else if (esDeLista(seccion)) {
            const marcados = ((valor as string[]) || []).map(convertir);
            if (marcados.length > 0) {
                filtros.push(new FilterAndData(seccion.filtro, marcados.length === 1 ? marcados[0] : marcados, propiedad));
            }
        } else {
            const raw = (valor as string) || '';
            const texto = limpiarTexto(seccion, raw);
            if (texto !== '') {
                filtros.push(new FilterAndData(seccion.filtro, texto, propiedad));
            }
        }
    }
    return filtros;
}

/**
 * Las etiquetas de «Filtros aplicados»: una por casilla marcada o valor elegido en el buscador (con la etiqueta de
 * su opción, si la hay; si no, la que dé `etiquetaConocida`, con la que el componente pasa las del buscador que ya
 * ha resuelto; si tampoco, el valor), una por texto escrito, una por sección de fechas («desde …», «hasta …» o el
 * rango) y una por Sí / No, solo si filtra (una sola marcada).
 */
export function filtrosAplicados(secciones: SeccionFiltro[], valores: ValoresPanel,
                                 etiquetaConocida?: (seccion: SeccionFiltro, valor: string) => string | undefined
): FiltroAplicado[] {
    const aplicados: FiltroAplicado[] = [];
    for (const seccion of secciones) {
        const valor = valores[seccion.propiedad];
        if (seccion.tipo === 'fechas') {
            const {desde, hasta} = (valor as RangoFechas) || SIN_FECHAS;
            const texto = desde && hasta ? `${diaLegible(desde)} – ${diaLegible(hasta)}`
                : desde ? `desde ${diaLegible(desde)}` : hasta ? `hasta ${diaLegible(hasta)}` : null;
            if (texto) {
                aplicados.push({seccion, valor: `${desde ?? ''}/${hasta ?? ''}`, texto: `${seccion.titulo}: ${texto}`});
            }
        } else if (seccion.tipo === 'sino') {
            const marcados = (valor as string[]) || [];
            if (marcados.length === 1) {
                const opcion = OPCIONES_SINO.find(o => o.valor === marcados[0]);
                aplicados.push({seccion, valor: marcados[0], texto: `${seccion.titulo}: ${opcion?.etiqueta ?? marcados[0]}`});
            }
        } else if (esDeLista(seccion)) {
            for (const marcado of (valor as string[]) || []) {
                const opcion = (seccion.opciones || []).find(o => o.valor === marcado);
                const etiqueta = opcion?.etiqueta ?? etiquetaConocida?.(seccion, marcado) ?? marcado;
                aplicados.push({seccion, valor: marcado, texto: `${seccion.titulo}: ${etiqueta}`});
            }
        } else {
            const raw = (valor as string) || '';
            const texto = limpiarTexto(seccion, raw);
            if (texto !== '') {
                aplicados.push({seccion, valor: texto, texto: `${seccion.titulo}: ${texto}`});
            }
        }
    }
    return aplicados;
}

/** Los valores sin lo de esa etiqueta (copia; no toca los de entrada) */
export function sinValor(valores: ValoresPanel, aplicado: FiltroAplicado): ValoresPanel {
    const propiedad = aplicado.seccion.propiedad;
    const actual = valores[propiedad];
    const tipo = aplicado.seccion.tipo;
    return {
        ...valores,
        // Las fechas se quitan enteras (una sola etiqueta por sección); en las de lista, solo ese valor
        [propiedad]: tipo === 'fechas' ? {...SIN_FECHAS}
            : esDeLista(aplicado.seccion) ? ((actual as string[]) || []).filter(v => v !== aplicado.valor)
            : ''
    };
}

/** Con `destacadas` vacía, cuántas opciones se ven antes de «Ver todos» */
export const MAX_SIN_DESTACADAS = 8;

/**
 * Las casillas que se pintan, en el orden de las opciones: todas si se ha pulsado «Ver todos» o la sección no
 * tiene `destacadas`; si no, las destacadas y las marcadas (una marcada no se esconde nunca: no se vería que
 * está filtrando).
 */
export function opcionesVisibles(seccion: SeccionFiltro, marcados: string[], verTodas: boolean): OpcionFiltro[] {
    const opciones = seccion.opciones || [];
    if (verTodas || seccion.destacadas === undefined) {
        return opciones;
    }
    const destacadas = seccion.destacadas.length > 0
        ? seccion.destacadas
        : opciones.slice(0, MAX_SIN_DESTACADAS).map(o => o.valor);
    return opciones.filter(o => destacadas.includes(o.valor) || marcados.includes(o.valor));
}

/** Cuántas casillas quedan tras «Ver todos»; con 0 no hay enlace */
export function opcionesOcultas(seccion: SeccionFiltro, marcados: string[], verTodas: boolean): number {
    return (seccion.opciones || []).length - opcionesVisibles(seccion, marcados, verTodas).length;
}

/**
 * Los filtros de los `porDefecto`, tal cual saldrían marcando esas casillas. Solo se pasan las secciones que
 * los tienen: así una sección sin valores por defecto no puede romper la apertura de la tabla.
 */
export function filtrosPorDefecto(secciones: SeccionFiltro[], propiedades: InternalPropertyMap[]): FilterAndData[] {
    const conDefecto = secciones.filter(s => s.tipo === 'casillas' && (s.porDefecto || []).length > 0);
    const valores: ValoresPanel = {};
    conDefecto.forEach(s => valores[s.propiedad] = [...s.porDefecto]);
    return filtrosDesdeValores(conDefecto, valores, propiedades);
}

/**
 * Separa los filtros de la URL en los del panel y el resto, que el panel no pinta pero hay que conservar en cada
 * navegación: los del botón activo (p. ej. el between de «REALIZADOS HOY» en Pedidos, que si no se leería como
 * desde / hasta de la sección de fechas de esa misma propiedad), los de propiedades fijas, la búsqueda general,
 * los de propiedades fuera del panel (`fueraDelPanelDeFiltros`) y, si se pasan las secciones, los que ninguna
 * reclama. Sin `secciones` eso último no se puede saber y se quedan en `panel`. Conserva el orden de la URL.
 */
export function filtrosDeBotonYFijos<B extends { filter: FilterAndData[] }>(
    filtrosUrl: FilterAndData[], botones: B[], fijos: InternalPropertyMap[], secciones?: SeccionFiltro[]
): { panel: FilterAndData[], resto: FilterAndData[] } {
    const sinBoton = sinFiltrosDeBoton(filtrosUrl, botonAplicado(botones, filtrosUrl));
    const clavesFijas = fijos.map(f => f.modelKey);
    const esDelPanel = (f: FilterAndData) =>
        sinBoton.includes(f)
        && f.filter !== GeneralSearchFilter
        && !clavesFijas.includes(f.property?.modelKey)
        && !f.property?.fueraDelPanelDeFiltros
        && (!secciones || secciones.some(s => esDeLaSeccion(s, f)));
    const panel: FilterAndData[] = [];
    const resto: FilterAndData[] = [];
    for (const filtro of filtrosUrl) {
        (esDelPanel(filtro) ? panel : resto).push(filtro);
    }
    return {panel, resto};
}

/**
 * Lo que va a la URL cuando cambia el panel: todo lo que no es suyo (el botón, la búsqueda general, los fijos, los de
 * fuera del panel y los que ninguna sección reclama, ver `filtrosDeBotonYFijos`) y, detrás, lo nuevo del panel, que
 * sustituye a lo anterior suyo. Con el panel vacío («Borrar todos») queda solo lo que no es suyo.
 */
export function filtrosAlCambiarPanel<B extends { filter: FilterAndData[] }>(
    filtrosUrl: FilterAndData[], botones: B[], fijos: InternalPropertyMap[], secciones: SeccionFiltro[],
    delPanel: FilterAndData[]
): FilterAndData[] {
    return [...filtrosDeBotonYFijos(filtrosUrl, botones, fijos, secciones).resto, ...delPanel];
}
